import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useNavigate } from 'react-router-dom';
import profileService from '../../services/profileService';
import ShareModal from '../shared/ShareModal';
import { hasVerifiedAgentResult } from '../nexa_me/nexaMeProfile';
import bsideAgentShareService, {
  buildAgentOgShareUrl,
  buildAgentSharePageUrl,
  type AgentShareProfile,
} from '../../services/bsideAgentShareService';
import {
  parseAgentNameCardLanguages,
} from './agentNameCardContent';

const PAGE_BG = '#FFFDFB';
const TEXT_DARK = '#363434';
const CARD_BG = '#4C403E';
const CARD_BUTTON_BG = 'rgba(255, 255, 255, 0.10)';
const CARD_BUTTON_BORDER = 'rgba(255, 255, 255, 0.42)';
const CARD_BUTTON_HOVER_BG = 'rgba(255, 255, 255, 0.40)';
const CARD_BUTTON_HOVER_BORDER = 'rgba(255, 255, 255, 0.62)';
const REQUEST_CONFIRMATION_PATH = '/business/request-confirmation';

type AgentNameCardPageProps = {
  overlay?: boolean;
  onClose?: () => void;
};

const actionButtonSx = {
  width: '100%',
  height: 48,
  borderRadius: '10px',
  border: `1px solid ${CARD_BUTTON_BORDER}`,
  backgroundColor: CARD_BUTTON_BG,
  color: '#FFFFFF',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1,
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  '& .MuiButton-startIcon': {
    mr: '7px',
  },
  '&:hover': {
    borderColor: CARD_BUTTON_HOVER_BORDER,
    backgroundColor: CARD_BUTTON_HOVER_BG,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.55)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
};

const badgeSx = {
  height: 19,
  px: '10px',
  borderRadius: '10px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '10px',
  fontWeight: 400,
  lineHeight: 1,
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unable to read QR code image.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read QR code image.'));
    reader.readAsDataURL(blob);
  });

const AgentNameCardPage: React.FC<AgentNameCardPageProps> = ({ overlay = false, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentProfile, setAgentProfile] = useState<AgentShareProfile | null>(null);
  const [agentUrl, setAgentUrl] = useState('');
  const [agentOgShareUrl, setAgentOgShareUrl] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState('');
  const [qrCodeFailed, setQrCodeFailed] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const agentPhone = String(agentProfile?.phone ?? '').trim();
  const agentEmail = String(agentProfile?.email ?? '').trim();
  const agentLanguages = parseAgentNameCardLanguages(agentProfile?.languages);

  useEffect(() => {
    let cancelled = false;

    const loadNameCard = async () => {
      setLoading(true);
      setError('');

      try {
        const userProfile = await profileService.getUserProfile();
        const profile = await bsideAgentShareService.getAgentProfile(userProfile.id);
        const shareUrl = buildAgentSharePageUrl(profile);
        const ogShareUrl = buildAgentOgShareUrl(profile);
        const qrRequest = {
          agentId: profile.userId || userProfile.id,
          agentUrl: shareUrl,
        };
        const imageUrl = bsideAgentShareService.buildAgentQrCodeImageUrl(qrRequest);
        let exportableImageUrl = imageUrl;

        try {
          const qrBlob = await bsideAgentShareService.getAgentQrCodeImage(qrRequest);
          exportableImageUrl = await blobToDataUrl(qrBlob);
        } catch (qrError) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[bside][name-card qr image inline failed]', qrError);
          }
        }

        if (cancelled) return;

        setAgentProfile(profile);
        setAgentUrl(shareUrl);
        setAgentOgShareUrl(ogShareUrl);
        setQrCodeFailed(false);
        setQrCodeImageUrl(exportableImageUrl);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load name card');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadNameCard();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!overlay || !onClose) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, overlay]);

  const handleDownloadNameCard = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);
    setActionMessage('');

    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const { toBlob } = await import('html-to-image');
      const blob = await toBlob(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: CARD_BG,
        filter: node => !(node instanceof HTMLElement && node.dataset.nameCardExportExclude === 'true'),
      });

      if (!blob) {
        throw new Error('Unable to create name card image.');
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'allroomie-name-card.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      setActionMessage('Name card downloaded.');
    } catch (err: any) {
      setActionMessage(err?.message || 'Unable to download name card.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRequestConfirmation = () => {
    onClose?.();
    navigate(REQUEST_CONFIRMATION_PATH);
  };

  const card = (
    <Box
      ref={cardRef}
      onClick={event => event.stopPropagation()}
      sx={{
        width: { xs: 'calc(100vw - 32px)', md: 800 },
        minHeight: { xs: 'auto', md: 422 },
        position: 'relative',
        borderRadius: { xs: '24px', md: '39px' },
        overflow: 'hidden',
        backgroundColor: CARD_BG,
        boxShadow: overlay ? '0px 22px 55px rgba(54, 52, 52, 0.24)' : '0px 22px 50px rgba(54, 52, 52, 0.18)',
      }}
    >
      {loading ? (
        <Box sx={{ minHeight: 422, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={34} sx={{ color: '#FFFFFF' }} />
        </Box>
      ) : error ? (
        <Box sx={{ minHeight: 422, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              maxWidth: 520,
              textAlign: 'center',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '16px',
              color: '#FFFFFF',
            }}
          >
            {error}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: { xs: 'auto', md: 422 },
            px: { xs: 3, sm: '61px' },
            pt: { xs: 4, sm: '81px' },
            pb: { xs: 4, sm: '70px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2.5, sm: 0 },
            }}
          >
            <Box
              component="img"
              src={agentProfile?.avatar || undefined}
              alt="Agent avatar"
              sx={{
                width: 98,
                height: 98,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />

            <Box
              sx={{
                ml: { xs: 0, sm: '43px' },
                width: { xs: '100%', sm: 307 },
                minWidth: 0,
                color: '#FFFFFF',
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: { xs: '30px', sm: '31px' },
                  fontWeight: 600,
                  lineHeight: '38px',
                  letterSpacing: 0,
                  color: '#FFFFFF',
                }}
              >
                {agentProfile?.nickname || 'Agent'}
              </Typography>
              <Typography
                sx={{
                  mt: '4px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: 0,
                  color: '#FFFFFF',
                }}
              >
                {agentProfile?.merchantName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: 0,
                  color: '#FFFFFF',
                }}
              >
                {agentProfile?.merchantAddress}
              </Typography>
              <Box sx={{ mt: '12px' }}>
                <Typography
                  sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: '18px',
                    letterSpacing: 0,
                    color: '#FFFFFF',
                  }}
                >
                  {agentPhone}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: '18px',
                    letterSpacing: 0,
                    color: '#FFFFFF',
                  }}
                >
                  {agentEmail}
                </Typography>
              </Box>
              <Box sx={{ mt: '19px', display: 'flex', alignItems: 'center', gap: '17px', flexWrap: 'wrap' }}>
                {hasVerifiedAgentResult(agentProfile) && <Box sx={{ ...badgeSx, backgroundColor: '#D5FFD9', color: '#315A35' }}>
                  Verified Agent
                </Box>}
                {agentLanguages.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {agentLanguages.map(language => (
                      <Box key={language} sx={{ ...badgeSx, backgroundColor: '#FFFFFF', color: '#333333' }}>
                        {language}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                ml: { xs: 0, sm: 'auto' },
                mt: { xs: 1, sm: '-34px' },
                width: { xs: 136, sm: 170 },
                height: { xs: 136, sm: 170 },
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                flexShrink: 0,
              }}
            >
              {qrCodeImageUrl && !qrCodeFailed ? (
                <Box
                  component="img"
                  src={qrCodeImageUrl}
                  alt="Agent QR code"
                  onError={() => setQrCodeFailed(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    px: 1.5,
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '12px',
                    lineHeight: '16px',
                    textAlign: 'center',
                    color: TEXT_DARK,
                  }}
                >
                  QR unavailable
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            data-name-card-export-exclude="true"
            sx={{
              mt: { xs: 4, sm: '59px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'stretch', sm: 'flex-start' },
              gap: { xs: 2, sm: '34px' },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon sx={{ width: 20, height: 20 }} />}
              onClick={handleDownloadNameCard}
              disabled={!agentProfile || !agentUrl || isDownloading}
              sx={{ ...actionButtonSx, width: { xs: '100%', sm: 182 }, flexShrink: 0 }}
            >
              {isDownloading ? 'Downloading...' : 'Download Card'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareOutlinedIcon sx={{ width: 16, height: 16 }} />}
              onClick={() => setShareModalOpen(true)}
              disabled={!agentOgShareUrl}
              sx={{ ...actionButtonSx, width: { xs: '100%', sm: 189 }, flexShrink: 0 }}
            >
              Share Availability
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendOutlinedIcon sx={{ width: 16, height: 16 }} />}
              onClick={handleRequestConfirmation}
              disabled={!agentProfile}
              sx={{ ...actionButtonSx, width: { xs: '100%', sm: 228 }, flexShrink: 0 }}
            >
              Request Confirmation
            </Button>
          </Box>

          {actionMessage && (
            <Typography
              data-name-card-export-exclude="true"
              role="status"
              sx={{
                mt: 2,
                minHeight: 18,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '12px',
                color: '#FFFFFF',
                textAlign: 'center',
                overflowWrap: 'anywhere',
              }}
            >
              {actionMessage}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const shareModal = (
    <ShareModal
      open={shareModalOpen}
      onClose={() => setShareModalOpen(false)}
      shareUrl={agentOgShareUrl}
      title={agentProfile?.nickname || 'Book an appointment'}
      description="Book an appointment on Allroomie."
      emailAction={agentProfile && agentUrl ? {
        send: recipientEmail => {
          const publicOrigin = new URL(agentUrl).origin;
          const brokerageImage = new URL(
            agentProfile.avatar || '',
            `${publicOrigin}/`
          ).toString();

          return bsideAgentShareService.shareBrokerageCardByEmail({
            recipientEmail,
            brokerageName: agentProfile.nickname?.trim() || 'Agent',
            brokerageCompanyName: agentProfile.merchantName?.trim() || '',
            brokerageImage,
            appointmentUrl: agentUrl,
            learnMoreUrl: publicOrigin,
          });
        },
      } : undefined}
      actions={['email', 'whatsapp', 'imessage']}
      zIndex={overlay ? 1901 : undefined}
    />
  );

  if (overlay) {
    return (
      <>
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Name card"
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1800,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: { xs: '104px', md: '166px' },
          px: 2,
          backgroundColor: 'rgba(255, 253, 251, 0.22)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          overflowY: 'auto',
        }}
      >
        {card}
      </Box>
      {shareModal}
      </>
    );
  }

  return (
    <>
    <Box
      sx={{
        minHeight: 'calc(100vh - var(--appbar-height, 64px))',
        backgroundColor: PAGE_BG,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        px: 2,
        py: { xs: 8, md: '166px' },
      }}
    >
      {card}
    </Box>
    {shareModal}
    </>
  );
};

export default AgentNameCardPage;
